import { DocumentCardActivity, Link, Separator, TooltipHost, Icon, Toggle, Checkbox, DefaultButton, ActionButton, SpinnerSize, PrimaryButton } from '@fluentui/react';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { AdditionalPermissionRole, PermissionRole, PowerAppEnvironmentType, PowerAppProvisioningStatus, PowerAppsScope, PowerAppStatus, PowerAppSupportType, PowerAppUsageEstimation, PowerAppUsageType } from '../../../GlobalConst/globalEnums';
import { mapEnumToOptions, mapUserForPeople, renderDateTime } from '../../../helpers/HelperMethods';
import InfoTooltip from '../../formFields/InfoTooltip';
import Person from '../Person';
import CustomDropDown from '../SiteEditElements/CustomDropDown';
import CustomTextField from '../SiteEditElements/CustomTextField';
import DimensionPicker from '../SiteEditElements/DimensionPicker';
import SecuredComponent from '../SiteEditElements/SecuredComponent';
import TabChangedDialog from '../TabChangedDialog';
import Warning from '../../CreateSite/CommonComponents/Warning';
import AppStoreSubmissionForm from './AppStoreSubmissionForm';
import Api from '../../../services/Api'
import LoadingIcon from '../../formFields/LoadingIcon';
import DeletePowerAppSolutionBtn from './DeletePowerAppSolutionBtn';
import DeletePowerAppSolutionConfirmationDialog from './DeletePowerAppSolutionConfirmationDialog';
import RestorePowerAppSolutionBtn from './RestorePowerAppSolutionBtn';

const SolutionDetails = ({ solution, disabled, onUpdate, onPersonUpdate, onDimensionUpdate, getValue, isEditEnabled = () => false, isSaveEnabled = () => false, isRecertification = () => false, isRestoreEnabled = () => false, btnEdit, btnSave, btnRecertify, messagePanel, showTabSwitchDialog, onDiscardDialog, onSaveDialog, onCancelDialog, getChange, onSaveCallback, updateSolution, showDeletionRequestSavedDialog, setShowDeletionRequestSavedDialog, isSaving, isRefreshed, isMainForm }) => {

    const currentUser = useSelector((state) => state.user.currentUser)
    const myOwnSolution = [solution.primaryOwner, solution.secondaryOwner].some(x => x && x.userId === currentUser.id) || solution.isOwnedViaServiceLeads || solution.isOwnedViaEnvironmentOwner

    const [childrenValidation, setChildrenValidation] = useState({})
    const [isChildrenUpdateValid, setIsChildrenUpdateValid] = useState(true)
    const [isRecertificationConfirmed, setIsRecertificationConfirmed] = useState(false)
    const [isSupportingSolution, setIsSupportingSolution] = useState(!!solution.supportedSolutionId)
    const [showAppStoreSubmissionFormDialog, setShowAppStoreSubmissionFormDialog] = useState(false)
    const [cmdbSolution, setCmdbSolution] = useState(solution.isCmdbSynced ? solution : undefined)
    const [isCmdbLoading, setIsCmdbLoading] = useState(false)
    const [cmdbMessage, setCmdbMessage] = useState()

    const isCmdbSolution = !!cmdbSolution

    useEffect(() => {
        if (currentUser.role === PermissionRole.administrator || solution.isOwnedViaServiceLeads || solution.isOwnedViaEnvironmentOwner)
            setIsChildrenUpdateValid(true)
        else
            setIsChildrenUpdateValid(Object.keys(childrenValidation).reduce((value, key) => {
                return value && childrenValidation[key];
            }, true))
    }, [childrenValidation])

    useEffect(() => {
        if (!isSupportingSolution && onDimensionUpdate)
            onDimensionUpdate('supportedSolutionId', 'supportedSolutionId', null, 'key', true)
    }, [isSupportingSolution])    

    const getFieldValue = (key, isNullableObject = false, defaultSet = null) =>{
        return isCmdbSolution ? cmdbSolution[key] : getValue(key, isNullableObject, defaultSet)
    } 

    const onUpdateChildrenValidation = (key, isValid) => {
        setChildrenValidation(valid => ({ ...valid, [key]: isValid }))
    }

    const getSecondaryOwnerError = () => {
        if (isCmdbSolution)
            return

        const primaryOwner = getValue('primaryOwner', true)
        const secondaryOwner = getValue('secondaryOwner', true)

        if (primaryOwner && primaryOwner.id) {
            const isSecondaryOwnerInvalid = !secondaryOwner || !secondaryOwner.id || secondaryOwner.isExternal || secondaryOwner.isServiceAccount;
        
            if (primaryOwner.isExternal && isSecondaryOwnerInvalid)
                return 'If Primary Owner is an External Account, Secondary Owner must be an ABB employee.'
        
            if (primaryOwner.isServiceAccount && isSecondaryOwnerInvalid)
                return 'If Primary Owner is a Service Account, Secondary Owner must be an ABB employee.'
        
            if (secondaryOwner && (primaryOwner.userId || primaryOwner.id) === (secondaryOwner.userId || secondaryOwner.id))
                return 'Secondary Owner must be different than Primary Owner.'
        }
    }

    const getSupportContact = () => {
        if (getValue('supportType') === PowerAppSupportType.myself) {
            const primaryOwner = getFieldValue('primaryOwner', true)
            return primaryOwner ? primaryOwner.email : ''
        }

        return getValue('supportContact')
    }

    const getEnvironmentPermissionWarning = (fieldName) => {
        if (isCmdbSolution)
            return

        const user = getValue(fieldName, true)
        if (user && user.powerPlatformEnvironments && !solution.environments.every(e => user.powerPlatformEnvironments.includes(e.internalId))) {
             return <div>Please note that new owner might not have access to all environments where this Solution is deployed. Additional access to environment can be requested <a href='https://myservices.abb.com/cs_gr?id=esc_sc_cat_item_guide&table=sc_cat_item&sys_id=bc2842b187cb9590dc10eb580cbb35cf&searchTerm=request%20access%20environment'>here</a>.</div>
        }

        return null
    }

    const isValid = (key) => childrenValidation[key] === undefined || childrenValidation[key]

    const isIcofrLimited = currentUser.role !== PermissionRole.administrator && solution && solution.environment.isIcofr

    const icofrWarningMessage = 
        isIcofrLimited &&
        <div>For ICoFR environments, ownership transfer must be requested by contacting support team: <a href={`mailto:global-storeandsharepowerplatformsupport@abb.com?subject=${`Ownership change requested for ${solution.environment.name} Power Platfrom Solution ${solution.name}`}`}>global-storeandsharepowerplatformsupport@abb.com</a>.</div>
    
    const ownerTooltipContent = 
        <div>
            Ownership transfer can be requested with or without granting access to artifacts.
            <ul>
                <li>
                    If permission update is not selected, the new owner will be updated in the provisioner database once the transfer is approved, but will not receive access to artifacts.
                </li>
                <li>
                    If a permission update is selected, the new owner will be granted access to the artifacts in the Power Platform environments upon approval.
                </li>
            </ul>
        </div>

    const recertificationCheckboxLabel =
        <div style={{marginLeft: 5}}>
            By Recertifying I confirm all information given is to the best of my understanding and I am adhering at all times to the Power Platform <a href="https://abb.sharepoint.com/sites/powerplatformhub/SitePages/KnowledgeBase/App-Maker-Agreement.aspx">App Maker Agreement</a> at ABB.
        </div>
    const recertificationCheckbox = solution && isRecertification() &&
        <div style={{position: 'relative'}}>
            <Checkbox required={true} onRenderLabel={() => recertificationCheckboxLabel} defaultChecked={false} onChange={(ev, checked) => setIsRecertificationConfirmed(checked)} styles={{root:{marginRight: 30}}} />
            {
                !isRecertificationConfirmed &&
                <Warning element='nolabel-small' title='Select to complete Recertification' />
            }
        </div>

    const getSupportedSolution = () => {
        const updateValue = getChange && getChange('supportedSolutionId')
        if (updateValue)
            return updateValue.value

        const key = getValue('supportedSolutionId');
        const value = getValue('supportedSolutionName');

        return key ? { key: key.toString(), value } : null;
    }

    const fetchSolutionFromCmdb = async () => {
        const cmdbId = getValue('cmdbId')
        if (!cmdbId)
            return

        setIsCmdbLoading(true)

        try {
            const result = await Api.get(`/EntryValidation/GetPowerAppSolutionFromCmdb?cmdbId=${cmdbId}`)
            
            const data = result.data
            setCmdbSolution(data)

            if (data) {
                onUpdate('isCmdbSynced', true)

                onPersonUpdate('primaryOwner', [data.primaryOwner], true)
                onPersonUpdate('secondaryOwner', [data.secondaryOwner], true)
                onDimensionUpdate('geography', 'geographyId', data.geography)
                onDimensionUpdate('management', 'managementId', data.management)
                //onDimensionUpdate('ardoq', 'ardoqIds', data.ardoq, 'key')
                onUpdate('description', data.description)
                onUpdate('numberOfUsers', data.numberOfUsers)
                onUpdate('usageType', data.usageType)
            } else {
                setCmdbMessage('CMDB ID was not found in CMDB.')
            }
        }
        catch (error) {
            setCmdbSolution(null)
            setCmdbMessage(`${error}: ${error.response.data.ExceptionMessage}`)
        }

        setIsCmdbLoading(false)
    }

    const onCmdbIdBlur = () => {
        const cmdbId = getValue('cmdbId')
        if (!cmdbId) {
            setCmdbSolution(undefined)
            onUpdate('isCmdbSynced', false)
        }
    }

    const getSolutionFromCmdbIcon = !disabled && 
        <div style={{minWidth: 32}}>
        {
            isCmdbLoading
            ? <div style={{marginTop: 6}}><LoadingIcon defaultHeight={true} spinnerSize={SpinnerSize.medium} /></div>
            : <TooltipHost content='Get data from CMDB'>
                <ActionButton styles={{root: {height: 32, width: 16}}} iconProps={{iconName: 'cloudDownload', styles: {root: {fontSize: 18}}}} onClick={fetchSolutionFromCmdb} />
            </TooltipHost>
        }
        </div>

    const cmdbFieldLockedIcon = isCmdbSolution &&
        <TooltipHost content='Property managed in CMDB. Please update it in CMDB and refresh or remove CMDB ID to unlock editing in Provisioner.' styles={{root: {color: '#ff9400', cursor: 'pointer', display: 'block'}}} >
            <Icon iconName='uneditable2' />
        </TooltipHost>
        
    const btnRestore =  
        <SecuredComponent 
            readSecurityLevel='Contributor'
            unlimitedAccess={solution && (solution.isOwnedViaServiceLeads || solution.isOwnedViaEnvironmentOwner)}
        >
            {
                <TooltipHost content={solution && solution.supportedSolutionId && 'This solution can be restored only when its supported solution has been restored.' }>
                    <PrimaryButton className="edit-icon" onClick={(ev) => updateSolution(ev, false, true)} disabled={solution && solution.supportedSolutionId}>Restore</PrimaryButton>
                </TooltipHost>
            }
        </SecuredComponent>

    const editor =
        <div className="site-editor">
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={twoColumnsContainer}>
                    <div style={twoColumnsSingleCol}>
                        <label className='item-title'>Name</label>
                        <Link
                            target='_blank'
                            href={solution.url}
                            styles={{ root: { display: 'block', padding: '8px', backgroundColor: 'rgb(243, 242, 241)' } }}
                        >
                            {solution.name}
                        </Link>
                    </div>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                            <label className='item-title'>CMDB ID</label>
                            <div style={{display: 'flex'}}>
                                <CustomTextField
                                    value={getValue('cmdbId')}
                                    disabled={disabled}
                                    onUpdate={(v) => onUpdate('cmdbId', v)}
                                    errorMessage={cmdbSolution === null && cmdbMessage}
                                    warningMessage={cmdbSolution && 'When a CMDB ID is provided, certain fields on the form become locked because their values are retrieved directly from the CMDB.'}
                                    onBlur={onCmdbIdBlur}
                                />
                                {getSolutionFromCmdbIcon}
                            </div>         
                        </div>
                    </SecuredComponent>
                </div>
                <div>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <label className='item-title'>Description <InfoTooltip content='The initial value is retrieved from the solution object in the environment but will not be synchronized with the Power Platform solution object. It will remain accessible only through the Provisioner tool.' /></label>
                        <CustomTextField
                            value={getFieldValue('description')}
                            disabled={disabled || isCmdbSolution}
                            required={true && !isCmdbSolution}
                            onUpdate={(v) => onUpdate('description', v)}
                            onValidation={(v) => onUpdateChildrenValidation('description', v)}
                            multiline={true}
                            additionalIcon={cmdbFieldLockedIcon}
                        />
                    </SecuredComponent>
                </div>
                <div>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <label className='item-title'>Dependencies <InfoTooltip content='Describe what other dependencies your solution/app/flow has (e.g. external systems dependencies, some specific environment configuration etc). Everything important that is not included inside component.' /></label>
                        <CustomTextField
                            value={getValue('dependencies')}
                            disabled={disabled}
                            onUpdate={(v) => onUpdate('dependencies', v)}
                            onValidation={(v) => onUpdateChildrenValidation('dependencies', v)}
                            multiline={true}
                        />
                    </SecuredComponent>
                </div>
                <div>
                    <SecuredComponent 
                        editSecurityLevel={isIcofrLimited ? 'Administrator' : 'Contributor'}
                        unlimitedAccess={myOwnSolution && !isIcofrLimited}
                        readOnlyAccess={true}
                    >
                        <Person
                            userType={<div>Primary Owner <InfoTooltip content={ownerTooltipContent} /></div>}
                            updateType="primaryOwner"
                            onChange={(v) => onPersonUpdate('primaryOwner', v, true)}
                            user={getFieldValue('primaryOwner', true)}
                            isRequired={true && !isCmdbSolution}
                            limited={true}
                            disabled={disabled || isSupportingSolution || isCmdbSolution}
                            onValidation={(v) => onUpdateChildrenValidation('primaryOwner', v)}
                            includePowerPlatformEnvironments={solution.environment.type !== PowerAppEnvironmentType.default}
                            warningMessage={getEnvironmentPermissionWarning('primaryOwner') || (!disabled && icofrWarningMessage)}
                            additionalIcon={cmdbFieldLockedIcon}
                        />
                        {
                            !disabled && getChange('primaryOwner') && getChange('primaryOwner').value && isValid('primaryOwner') && !isCmdbSolution &&
                            <Toggle 
                                onText='Transfer ownership and update components permissions' 
                                offText='Transfer only ownership' 
                                defaultChecked={!solution.environment.isIcofr} 
                                onChange={(ev, checked) => onUpdate('primaryOwnerTransferOnlyOwnership', !checked)} 
                                disabled={solution.environment.isIcofr}
                            />
                        }
                    </SecuredComponent>
                </div>
                <div style={twoColumnsContainer}>
                    <SecuredComponent 
                        editSecurityLevel={isIcofrLimited ? 'Administrator' : 'Contributor'}
                        unlimitedAccess={myOwnSolution && !isIcofrLimited}
                        readOnlyAccess={true}
                    >
                        <div style={twoColumnsSingleCol}>
                            <Person
                                userType={<div>Secondary Owner <InfoTooltip content={ownerTooltipContent} /></div>}
                                updateType="secondaryOwner"
                                onChange={(v) => onPersonUpdate('secondaryOwner', v, true)}
                                user={getFieldValue('secondaryOwner', true)}
                                isRequired={false}
                                limited={true}
                                disabled={disabled || isSupportingSolution || isCmdbSolution}
                                onValidation={(v) => onUpdateChildrenValidation('secondaryOwner', v)}
                                errorMessage={getSecondaryOwnerError()}
                                includePowerPlatformEnvironments={solution.environment.type !== PowerAppEnvironmentType.default}
                                warningMessage={getEnvironmentPermissionWarning('secondaryOwner') || (!disabled && icofrWarningMessage)}
                                additionalIcon={cmdbFieldLockedIcon}
                            />
                            {
                                !disabled && getChange('secondaryOwner') && getChange('secondaryOwner').value && isValid('secondaryOwner') && !isCmdbSolution &&
                                <Toggle 
                                    onText='Transfer ownership and update components permissions' 
                                    offText='Transfer only ownership' 
                                    defaultChecked={!solution.environment.isIcofr} 
                                    onChange={(ev, checked) => onUpdate('secondaryOwnerTransferOnlyOwnership', !checked)} 
                                    disabled={solution.environment.isIcofr}
                                />
                            }
                        </div>
                    </SecuredComponent>
                    <SecuredComponent
                        editSecurityLevel={isIcofrLimited ? 'Administrator' : 'Contributor'}
                        unlimitedAccess={myOwnSolution && !isIcofrLimited}
                        readOnlyAccess={true}
                    >
                        <div style={twoColumnsSingleCol}>
                            <Person
                                userType='Technical Owner'
                                updateType="technicalOwner"
                                onChange={(v) => onPersonUpdate('technicalOwner', v, true)}
                                user={getValue('technicalOwner', true)}
                                isRequired={false}
                                limited={true}
                                disabled={disabled}
                                onValidation={(v) => onUpdateChildrenValidation('technicalOwner', v)}
                            />
                            {
                                !disabled && getChange('technicalOwner') && isValid('technicalOwner') &&
                                <Toggle 
                                    onText='Transfer ownership and update components permissions'
                                    offText='Transfer only ownership' 
                                    defaultChecked={!solution.environment.isIcofr} 
                                    onChange={(ev, checked) => onUpdate('technicalOwnerTransferOnlyOwnership', !checked)} 
                                    disabled={solution.environment.isIcofr}
                                />
                            }
                        </div>
                    </SecuredComponent>
                </div>
                <div style={twoColumnsContainer}>
                    <SecuredComponent readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                            <DimensionPicker
                                itemSelector={(v) => onDimensionUpdate('geography', 'geographyId', v)}
                                searchTitle="Country"
                                searchDataUrl='/Geography'
                                isDisabled={disabled || isCmdbSolution}
                                selection={getFieldValue('geography')}
                                isRequired={true && !isCmdbSolution}
                                treeDataUrl='/Geography'
                                includeCrossOrg={true}
                                //additionalOptions={[{ key: 'Cross', value: 'Cross Organisation' }]}
                                onValidation={(v) => onUpdateChildrenValidation('geography', v)}
                                additionalIcon={cmdbFieldLockedIcon}
                            />
                        </div>
                    </SecuredComponent>
                    <SecuredComponent readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                            <DimensionPicker
                                itemSelector={(v) => onDimensionUpdate('management', 'managementId', v)}
                                searchTitle="Business owner organization"
                                searchDataUrl='/Management'
                                isDisabled={disabled || isCmdbSolution}
                                selection={getFieldValue('management')}
                                isRequired={true && !isCmdbSolution}
                                treeDataUrl='/Management'
                                onValidation={(v) => onUpdateChildrenValidation('management', v)}
                                additionalIcon={cmdbFieldLockedIcon}
                            />
                        </div>
                    </SecuredComponent>
                </div>
                <SecuredComponent readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                    <Separator styles={{ root: { marginTop: 15 } }} />
                </SecuredComponent>
                <div style={twoColumnsContainer}>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                            <label className='item-title'>Usage <InfoTooltip content={<span>More about classification can be found <a href='https://abb.sharepoint.com/sites/GlobalArchitecturePortal/SitePages/Architecture-Explained-CCD-Apps.aspx'>here</a>.</span>} /></label>
                            <CustomDropDown
                                options={mapEnumToOptions(PowerAppUsageType)}
                                value={getFieldValue('usageType')}
                                disabled={disabled || isCmdbSolution}
                                onUpdate={(v) => onUpdate('usageType', v)}
                                required={true && !isCmdbSolution}
                                onValidation={(v) => onUpdateChildrenValidation('usageType', v)}
                                additionalIcon={cmdbFieldLockedIcon}
                            />
                        </div>
                    </SecuredComponent>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                            <label className='item-title'>Estimated number of users</label>
                            <CustomDropDown
                                options={mapEnumToOptions(PowerAppUsageEstimation)}
                                value={getFieldValue('numberOfUsers')}
                                disabled={disabled || isCmdbSolution}
                                onUpdate={(v) => onUpdate('numberOfUsers', v)}
                                required={true && !isCmdbSolution}
                                onValidation={(v) => onUpdateChildrenValidation('numberOfUsers', v)}
                                additionalIcon={cmdbFieldLockedIcon}
                            />
                        </div>
                    </SecuredComponent>
                </div>
                <div style={twoColumnsContainer}>
                    <SecuredComponent readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                        <DimensionPicker
                            itemSelector={(v) => onDimensionUpdate('ardoq', 'ardoqIds', v, 'key')}
                            searchTitle="Business capability"
                            searchDataUrl='/Ardoq'
                            isDisabled={disabled}
                            selection={getValue('ardoq')}
                            isRequired={false}
                            treeDataUrl='/Ardoq'
                            keyHidden={true}
                            isMultiValue={true}
                        />
                        </div>
                    </SecuredComponent>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                            <label className='item-title'>Scope</label>
                            <CustomDropDown
                                options={mapEnumToOptions(PowerAppsScope)}
                                value={getValue('scope')}
                                disabled={disabled}
                                onUpdate={(v) => onUpdate('scope', v)}
                                required={true}
                                onValidation={(v) => onUpdateChildrenValidation('scope', v)}
                            />
                        </div>
                    </SecuredComponent>
                </div>
                <SecuredComponent readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                    <Separator styles={{ root: { marginTop: 15 } }} />
                </SecuredComponent>
                <div style={twoColumnsContainer}>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                            <label className='item-title'>Support type</label>
                            <CustomDropDown
                                options={mapEnumToOptions(PowerAppSupportType)}
                                value={getValue('supportType')}
                                disabled={disabled}
                                onUpdate={(v) => onUpdate('supportType', v)}
                                required={true}
                                onValidation={(v) => onUpdateChildrenValidation('supportType', v)}
                            />
                        </div>
                    </SecuredComponent>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                            <label className='item-title'>Support contact</label>
                            <CustomTextField
                                value={getSupportContact()}
                                disabled={disabled || getValue('supportType') === PowerAppSupportType.myself}
                                onUpdate={(v) => onUpdate('supportContact', v)}
                                required={getValue('supportType') !== PowerAppSupportType.myself || !isCmdbSolution}
                                onValidation={(v) => onUpdateChildrenValidation('supportContact', v)}
                                validationFormat={getValue('supportType') === PowerAppSupportType.sharedMailbox && 'email'}
                            />
                        </div>
                    </SecuredComponent>
                </div>
                <SecuredComponent
                    readSecurityLevel='Reader'
                    editSecurityLevel='Contributor'
                    unlimitedAccess={myOwnSolution}
                >
                    <label className='item-title'>Help page <InfoTooltip content='Link to the support page if applicable'/></label>
                    <CustomTextField
                        value={getValue('helpUrl')}
                        disabled={disabled}
                        onUpdate={(v) => onUpdate('helpUrl', v)}
                        onValidation={(v) => onUpdateChildrenValidation('helpUrl', v)}
                        validationFormat='url'
                    />
                </SecuredComponent>
                <Separator styles={{ root: { marginTop: 15 } }} />
                <div style={twoColumnsContainer}>
                    <SecuredComponent
                        readSecurityLevel='Reader'
                        editSecurityLevel='Contributor'
                        unlimitedAccess={myOwnSolution}
                    >
                        <div style={twoColumnsSingleCol}>
                            <label className='item-title'>Is supporting solution?</label>
                            <TooltipHost styles={{root: {display: 'inline-block', width: 75}}} content={solution.hasSupportingSolutions ? 'This solution has already other supporting solutions and cannot be set as supporting solution.' : ''}>
                                <Toggle
                                    onText='Yes'
                                    offText='No'
                                    defaultChecked={isSupportingSolution}
                                    disabled={disabled || solution.hasSupportingSolutions}
                                    onChange={(e, v) => setIsSupportingSolution(v)}
                                    styles={{root: { marginTop: 11 }}}
                                />
                            </TooltipHost>
                        </div>
                        {
                            isSupportingSolution &&
                            <div style={twoColumnsSingleCol}>
                                <DimensionPicker
                                    itemSelector={(v) => onDimensionUpdate('supportedSolutionId', 'supportedSolutionId', v, 'key', true)}
                                    searchTitle={<span>Supported Solution <InfoTooltip content='Select the solution that is supported by the current solution.' /></span>}
                                    searchDataUrl={`/PowerAppSolutionSearch?solutionId=${solution.id}&environmentId=${solution.environmentId}${solution.primaryOwner ? `&primaryOwnerId=${solution.primaryOwner.id}` : ''}${solution.secondaryOwner ? `&secondaryOwnerId=${solution.secondaryOwner.id}` : ''}`}
                                    isDisabled={disabled}
                                    selection={getSupportedSolution()}
                                    isRequired={true}
                                    treeDataUrl={`/PowerAppSolutionSearch?solutionId=${solution.id}&environmentId=${solution.environmentId}${solution.primaryOwner ? `&primaryOwnerId=${solution.primaryOwner.id}` : ''}${solution.secondaryOwner ? `&secondaryOwnerId=${solution.secondaryOwner.id}` : ''}`}
                                    onValidation={(v) => onUpdateChildrenValidation('supportedSolutionId', v)}
                                    isMultiValue={false}
                                    keyHidden={true}
                                    emptyMessage='No other solutions with the same owners are available in this environment.'
                                />
                            </div>
                        }
                    </SecuredComponent>
                </div>
                <Separator styles={{ root: { marginTop: 15 } }} />
                <div className='item-title'>
                    Status: {solution.status}
                    {
                        solution.provisioningStatus === PowerAppProvisioningStatus.expired &&
                        <TooltipHost content='Artifact still exists on environment but recertification expired.'>
                            <Icon iconName="Warning" className='warningIcon' tooltipProps={{styles: {root: {maxWidth: 400}}}} />
                        </TooltipHost>
                    }
                </div>
                <div style={{marginTop: 10}}>
                    {recertificationCheckbox}
                    <div style={{...twoColumnsContainer, alignItems: 'center'}}>
                        <div style={twoColumnsSingleCol}>
                            <label className='item-title'>Last recertification:</label>
                            <DocumentCardActivity
                                activity={solution.lastRecertificationDate ? `Last recertified on ${renderDateTime(solution.lastRecertificationDate)}` : 'No recertification'}
                                people={mapUserForPeople(solution.lastRecertifiedBy)}
                            />
                        </div>
                        <div>
                            {isRecertification() && btnRecertify(isChildrenUpdateValid && isRecertificationConfirmed)}
                            {isRestoreEnabled() &&                              
                                <SecuredComponent 
                                    readSecurityLevel='Contributor'
                                    unlimitedAccess={myOwnSolution}
                                >
                                    {btnRestore}
                                </SecuredComponent>
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>

    const highestUnmanagedSolution = solution && solution.groupedSolutions ? solution.groupedSolutions.find(x => !x.isManagedSolution) : null

    const btnPublishToCatalog =
        isMainForm &&
        solution.status === PowerAppStatus.active &&
        currentUser.additionalPermissionRoles && currentUser.additionalPermissionRoles.includes(AdditionalPermissionRole.powerPlatfromAppStorePilot) &&
        ([solution.primaryOwner, solution.secondaryOwner].some(x => x && x.userId === currentUser.id) || currentUser.role === PermissionRole.administrator) &&
        <div style={{float: 'right', marginRight: '-12px', marginLeft: '20px'}}>
            <TooltipHost content={!highestUnmanagedSolution && 'Unmanaged Solution was not found for this Solution. Only unmanaged Solutions can be published to App Catalog.'} >
                <DefaultButton onClick={e => setShowAppStoreSubmissionFormDialog(true)} text='Publish to catalog' disabled={!highestUnmanagedSolution} />
            </TooltipHost>
             {
                highestUnmanagedSolution &&
                <AppStoreSubmissionForm solution={highestUnmanagedSolution} submissionItems={solution.appStoreSubmissionItems} showDialog={showAppStoreSubmissionFormDialog} setShowDialog={setShowAppStoreSubmissionFormDialog} onSaveCallback={() => onSaveCallback(false)}/>
             }
        </div>    
        
    const isDeleteEnabled = () => 
        isMainForm
        && solution.status !== PowerAppProvisioningStatus.deleted 
        && (currentUser.role === PermissionRole.administrator || (solution.provisioningStatus !== PowerAppProvisioningStatus.updating && solution.provisioningStatus !== PowerAppProvisioningStatus.pendingApproval))
        && currentUser.additionalPermissionRoles && currentUser.additionalPermissionRoles.includes(AdditionalPermissionRole.powerPlatfromAppStorePilot)


    const isRestoreDeletedEnabled = () => {
        return isMainForm
            && !isSaving 
            && !isRefreshed
            && solution.isDeletedFromProvisioner
            && solution.groupedSolutions.some(x => x.backupSolutionUrl)
            && solution.provisioningStatus === PowerAppProvisioningStatus.deleted 
            && (currentUser.role === PermissionRole.administrator || (solution.provisioningStatus !== PowerAppProvisioningStatus.updating && solution.provisioningStatus !== PowerAppProvisioningStatus.pendingApproval))
            && currentUser.additionalPermissionRoles && currentUser.additionalPermissionRoles.includes(AdditionalPermissionRole.powerPlatfromAppStorePilot)
    }

    const editPanel = 
        <div>
            <div style={{ padding: '0 20px'}}>
                <SecuredComponent readSecurityLevel='Administrator'
                    editSecurityLevel='Administrator'
                    unlimitedAccess={myOwnSolution}>
                    <div style={{ width: '100%', marginTop: '20px'}}>
                        {
                            isEditEnabled() && 
                            btnEdit
                        }
                        {
                            !disabled && !isRecertification() && 
                            btnSave(isSaveEnabled() && isChildrenUpdateValid)
                        }
                        {
                            isDeleteEnabled() &&
                            <DeletePowerAppSolutionBtn solution={solution} onSaveCallback={() => onSaveCallback(false)} updateSolution={updateSolution} />
                        }
                        {
                            showDeletionRequestSavedDialog &&
                            <DeletePowerAppSolutionConfirmationDialog solution={solution} onDialogClosed={() => setShowDeletionRequestSavedDialog(false)} />
                        }
                        {
                            isRestoreDeletedEnabled() &&
                            <RestorePowerAppSolutionBtn solution={solution} updateSolution={updateSolution} />
                        }
                        {
                            btnPublishToCatalog
                        }
                    </div>
                </SecuredComponent>
            </div>
            {messagePanel}
        </div>

    return (
        <div>
            {editor}
            <Separator />
            {editPanel}
            <TabChangedDialog
                isSaveEnabled={isSaveEnabled() && isChildrenUpdateValid}
                showDialog={showTabSwitchDialog}
                onDiscardDialog={onDiscardDialog}
                onSaveDialog={onSaveDialog}
                onCancelDialog={onCancelDialog}
            />
        </div>
    )
}

const twoColumnsContainer = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    gap: 20,
}

const twoColumnsSingleCol = {
    //width: '295px'
    flex: 1,
    overflow: 'hidden'
}

export default SolutionDetails;


// WEBPACK FOOTER //
// ./src/components/EditSite/PowerAppEditElements/SolutionDetails.js